<template>
  <div
    class="date-picker"
    v-on-click-outside="changeCalendarOpenState"
  >
    <DatePicker
      color="indigo"
      is-range
      borderless
      v-model="dateRange"
      :is-dark="props.isDark"
      :initial-page="page"
      :min-date="toJsDate(minDate)"
      :max-date="toJsDate(maxDate)"
      :locale="props.lang"
    />
  </div>
</template>
<script setup lang="ts">
import 'v-calendar/dist/style.css'

import { vOnClickOutside } from '@vueuse/components'
import { DateTime } from 'luxon'
import { DatePicker } from 'v-calendar'
import { DatePickerRangeObject } from 'v-calendar/dist/types/src/use/datePicker'
import { computed, onBeforeMount, ref, watch } from 'vue'

import { DATE_FORMAT } from './period-picker.constant'

export type BcDateRangeProps = {
  isDark: boolean
  lang: 'ko' | 'en'
  dateFormat: DATE_FORMAT
  isOpen: boolean
  range: {
    start: string
    end: string
  }
  minDate: string
  maxDate: string
  calendarPosition: 'top' | 'bottom'
}
const props = withDefaults(defineProps<BcDateRangeProps>(), {
  minDate: '2009-01-01',
  maxDate: DateTime.now().toFormat(DATE_FORMAT.DEFAULT_FORMAT),
})
const emits = defineEmits([ 'update:isOpen', 'changeStDateEndDate' ])
// TODO: 타입 오류로 빌드가 되지않아 임시로 타입 캐스팅으로 수정했습니다. 추 후 리팩토링이 필요합니다.
const dateRange = ref<DatePickerRangeObject>({
  start: null as any,
  end: null as any,
})

const toJsDate = (date: string) => DateTime.fromFormat(date, props.dateFormat).toJSDate()

const page = computed(()=> {
  const endDate = DateTime.fromFormat(props.range.end, props.dateFormat)
  return {
    month: endDate.month,
    year: endDate.year
  }
})

const marginTop = computed(()=> {
  if (props.calendarPosition === 'top') {
    return '-320px'
  }
  return '8px'
})

const changeCalendarOpenState = () => {
  if (props.isOpen) {
    emits('update:isOpen', false)
  }
}

onBeforeMount(()=> {
  dateRange.value.start = DateTime.fromFormat(props.range.start, props.dateFormat).toJSDate()
  dateRange.value.end = DateTime.fromFormat(props.range.end, props.dateFormat).toJSDate()
})

watch(()=>dateRange.value, ()=> {
  if (dateRange.value.start !== null && dateRange.value.end !== null) {
    emits('changeStDateEndDate', {
      start: DateTime.fromJSDate(dateRange.value.start as Date).toFormat(props.dateFormat),
      end: DateTime.fromJSDate(dateRange.value.end as Date).toFormat(props.dateFormat)
    })
  }
})
</script>

<style scoped lang="scss">
.date-picker {
  width: max-content;
  height: max-content;
  position: absolute;
  margin-top: v-bind(marginTop);
  right: 0;
  z-index: 2000;
  border-radius: 8px;

  :deep(.vc-container) {
    font-family: 'Inter', 'Noto Sans KR', sans-serif;
    border: 1px solid rgba(var(--bc-gray), var(--o02));
    border-radius: 8px;
    background-color: rgba(var(--bc-black), var(--o1));
  }

  :deep(.vc-day-content:hover) {
    background-color: rgba(var(--bc-theme), var(--o01)) !important;
  }

  :deep(.vc-day-layer ~ .vc-day-layer > .vc-highlight) {
    background-color: rgba(var(--bc-theme), var(--o1)) !important;
  }

  :deep(.vc-highlights .vc-day-layer ~ .vc-day-layer > .vc-highlight) {
    border: rgba(var(--bc-dark), var(--o1)) !important;
    color: rgba(var(--bc-absoluteWhite), var(--o1)) !important;
  }

  :deep(.vc-highlights + span) {
    border: rgba(var(--bc-dark), var(--o1)) !important;
    color: rgba(var(--bc-absoluteWhite), var(--o1)) !important;
  }

  :deep(.vc-is-dark .vc-weekday) {
    color: rgba(var(--bc-gray), var(--o08)) !important;
  }

  :deep(.vc-day-layer:first-child > .vc-highlight) {
    background-color: rgba(var(--bc-theme), var(--o04)) !important;
    border-color: rgba(var(--bc-theme), var(--o1)) !important;
  }

  :deep(.vc-arrow), :deep(.vc-title) {
    background: none;
  }
}
</style>